import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Bot,
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  Settings,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Eye
} from 'lucide-react';
import { PropBot } from '@/types/prop-bots';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { usePropBotDetailAdmin } from '@/hooks/use-prop-bot-detail-admin';
import { usePropBotOperations } from '@/hooks/use-prop-bot-operations';
import { useBotAccountReassignment } from '@/hooks/use-bot-account-reassignment';
import { AccountOperationsToolbar } from './AccountOperationsToolbar';
import { EnhancedAccountCard } from './EnhancedAccountCard';
import { PropBotAnalyticsDashboard } from './analytics/PropBotAnalyticsDashboard';
import { ReassignAccountDialog } from './ReassignAccountDialog';

interface PropBotDetailModalProps {
  bot: PropBot | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PropBotDetailModal: React.FC<PropBotDetailModalProps> = ({
  bot,
  isOpen,
  onClose,
}) => {
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');

  const {
    connectedAccounts,
    accountsStats,
    isLoading,
    refetch,
    forceRefresh,
    syncBalances,
    isSyncingBalances,
    syncResult,
    clearCache,
    isClearingCache,
    cacheStats
  } = usePropBotDetailAdmin(bot?.id);

  const {
    enableAccount,
    disableAccount,
    disconnectAccount,
    performBulkEnable,
    performBulkDisable,
    performBulkDisconnect,
    exportAccounts,
    isProcessing
  } = usePropBotOperations(bot?.id);

  const {
    isReassignDialogOpen,
    selectedAccountId,
    availableBots,
    isLoadingBots,
    isAdmin,
    openReassignDialog,
    closeReassignDialog,
    performReassignment,
    isReassigning
  } = useBotAccountReassignment({ currentBotId: bot?.id });


  const handleSyncBalances = async () => {
    if (!bot?.id) return;
    
    try {
      const result = await syncBalances(bot.id);
      if (result.success) {
        toast.success(`Balance sync completed: ${result.synced} accounts synced, ${result.failed} failed`);
      } else {
        toast.error(`Balance sync failed: ${result.errors.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to sync balances');
    }
  };

  const handleForceRefresh = async () => {
    try {
      await forceRefresh();
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    }
  };

  const handleClearCache = async () => {
    try {
      await clearCache();
      toast.success('Cache cleared successfully');
    } catch (error) {
      toast.error('Failed to clear cache');
    }
  };

  // Filter and search logic
  const filteredAccounts = useMemo(() => {
    return connectedAccounts.filter(account => {
      // Search filter
      const matchesSearch = searchTerm === '' || 
        account.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.accountNumber?.toLowerCase().includes(searchTerm.toLowerCase());

      // Status filter
      const matchesStatus = statusFilter === 'all' || account.status === statusFilter;

      // Account type filter
      let matchesType = true;
      if (accountTypeFilter === 'live') matchesType = account.isLive;
      else if (accountTypeFilter === 'demo') matchesType = !account.isLive;
      else if (accountTypeFilter === 'reverse') matchesType = account.isReversePosition;
      else if (accountTypeFilter === 'normal') matchesType = !account.isReversePosition;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [connectedAccounts, searchTerm, statusFilter, accountTypeFilter]);

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAccounts(new Set(filteredAccounts.map(account => account.id)));
    } else {
      setSelectedAccounts(new Set());
    }
  };

  const handleAccountSelection = (accountId: string, selected: boolean) => {
    const newSelection = new Set(selectedAccounts);
    if (selected) {
      newSelection.add(accountId);
    } else {
      newSelection.delete(accountId);
    }
    setSelectedAccounts(newSelection);
  };

  // Account operations
  const handleSingleAccountEnable = async (accountId: string) => {
    try {
      await enableAccount(accountId);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSingleAccountDisable = async (accountId: string) => {
    try {
      await disableAccount(accountId);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleSingleAccountDisconnect = async (accountId: string) => {
    try {
      await disconnectAccount(accountId);
      // Remove from selection if it was selected
      const newSelection = new Set(selectedAccounts);
      newSelection.delete(accountId);
      setSelectedAccounts(newSelection);
    } catch (error) {
      // Error handled by hook
    }
  };

  // Bulk operations
  const handleBulkEnable = async () => {
    try {
      await performBulkEnable(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleBulkDisable = async () => {
    try {
      await performBulkDisable(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleBulkDisconnect = async () => {
    try {
      await performBulkDisconnect(Array.from(selectedAccounts));
      setSelectedAccounts(new Set());
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleExport = () => {
    exportAccounts(filteredAccounts);
  };

  const handleRefreshSingleBalance = async (accountId: string) => {
    // For individual balance refresh, we can trigger the sync for that specific account
    try {
      await handleSyncBalances();
    } catch (error) {
      // Error handled by sync
    }
  };

  if (!bot) return null;

  const getBotStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </Badge>
        );
      case 'inactive':
        return (
          <Badge variant="secondary">
            <AlertCircle className="h-3 w-3 mr-1" />
            Inactive
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status}
          </Badge>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[95vw] lg:!w-[80vw] !max-w-none max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Bot className="h-6 w-6 text-purple-500" />
            <div>
              <div className="font-semibold">{bot.name}</div>
              <div className="text-sm text-muted-foreground font-normal">
                Prop Trading Bot Management
              </div>
            </div>
            {getBotStatusBadge(bot.status)}
          </DialogTitle>
        </DialogHeader>

        <div className="custom-scrollbar scrollbar-thin scrollbar-thumb-border/40 scrollbar-track-transparent hover:scrollbar-thumb-border/60 overflow-y-auto max-h-[75vh] space-y-6">
          {/* Quick Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Connected Accounts</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : accountsStats?.totalAccounts || 0}
                    </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : accountsStats?.uniqueUsers || 0}
                    </p>
                  </div>
                  <Activity className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Capital</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? (
                        <Skeleton className="h-8 w-20" />
                      ) : (
                        `$${(accountsStats?.totalCapital || 0).toLocaleString()}`
                      )}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Reverse Positions</p>
                    <p className="text-2xl font-bold">
                      {isLoading ? <Skeleton className="h-8 w-12" /> : accountsStats?.reversePositions || 0}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bot Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Bot Information & Management
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleForceRefresh}
                    disabled={isLoading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSyncBalances}
                    disabled={isSyncingBalances}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${isSyncingBalances ? 'animate-spin' : ''}`} />
                    Sync Balances
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Bot ID</span>
                    <span className="text-sm font-mono">{bot.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <Badge variant="outline">Prop Trading</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Created</span>
                    <span className="text-sm">
                      {formatDistanceToNow(new Date(bot.created_date), { addSuffix: true })}
                    </span>
                  </div>
                </div>
                
                {bot.propDetails?.[0] && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Prop Firm</span>
                      <Badge variant="secondary">{bot.propDetails[0].prop_firm}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Pass Rate</span>
                      <span className="text-sm font-medium text-blue-600">
                        {((bot.propDetails[0].pass_rate || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Min Capital</span>
                      <span className="text-sm font-medium">
                        ${Number(bot.propDetails[0].min_capital || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Analytics Dashboard */}
          <PropBotAnalyticsDashboard
            botId={bot.id}
            botName={bot.name}
            connectedAccounts={connectedAccounts}
            isLoading={isLoading}
          />

          {/* Connected Accounts with Operations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Connected Trading Accounts
                </span>
                {filteredAccounts.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedAccounts.size === filteredAccounts.length && filteredAccounts.length > 0}
                      onCheckedChange={handleSelectAll}
                      disabled={isProcessing}
                    />
                    <span className="text-sm text-muted-foreground">Select All</span>
                  </div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-full" />
                          <div>
                            <Skeleton className="h-4 w-32 mb-2" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                        <div className="text-right">
                          <Skeleton className="h-6 w-20 mb-2" />
                          <Skeleton className="h-4 w-16" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : connectedAccounts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p>No connected accounts found</p>
                  <p className="text-sm mt-2">This bot has not been connected to any trading accounts yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Operations Toolbar */}
                  <AccountOperationsToolbar
                    accounts={connectedAccounts}
                    selectedAccounts={selectedAccounts}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    accountTypeFilter={accountTypeFilter}
                    onAccountTypeFilterChange={setAccountTypeFilter}
                    onBulkEnable={handleBulkEnable}
                    onBulkDisable={handleBulkDisable}
                    onBulkDisconnect={handleBulkDisconnect}
                    onExport={handleExport}
                    isProcessing={isProcessing}
                  />

                  {/* Account Cards */}
                  {filteredAccounts.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Eye className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p>No accounts match your filters</p>
                      <p className="text-sm mt-2">Try adjusting your search or filter criteria.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredAccounts.map((account) => (
                        <EnhancedAccountCard
                          key={account.id}
                          account={account}
                          isSelected={selectedAccounts.has(account.id)}
                          onSelectionChange={(selected) => handleAccountSelection(account.id, selected)}
                          onEnable={() => handleSingleAccountEnable(account.id)}
                          onDisable={() => handleSingleAccountDisable(account.id)}
                          onDisconnect={() => handleSingleAccountDisconnect(account.id)}
                          onRefreshBalance={() => handleRefreshSingleBalance(account.id)}
                          onReassign={isAdmin ? () => openReassignDialog(account.id) : undefined}
                          currentBot={bot}
                          isProcessing={isProcessing}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Reassign Account Dialog */}
        {bot && (
          <ReassignAccountDialog
            isOpen={isReassignDialogOpen}
            onClose={closeReassignDialog}
            accountId={selectedAccountId}
            currentBot={bot}
            availableBots={availableBots}
            isLoadingBots={isLoadingBots}
            isReassigning={isReassigning}
            isAdmin={isAdmin}
            onReassign={performReassignment}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};